import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { RootLayout, ContentLayout } from "@/layouts";
import { ProtectedRoute, AuthGuard } from "@/components/auth";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { CommonLoading } from "@/components/shared/common-loading";

// 인증 페이지는 초기 진입 경로이므로 lazy 처리하지 않음
import LoginPage from "@/app/auth/login/page";
import SignupPage from "@/app/auth/signup/page";
import FindIdPage from "@/app/auth/find-id/page";
import FindPasswordPage from "@/app/auth/find-password/page";

// 로그인 이후 접근하는 페이지들은 React.lazy로 코드 스플리팅
const MainPage = lazy(() => import("@/app/(content)/main/page"));
const MypagePage = lazy(() => import("@/app/(content)/mypage/page"));
const VocabularyPage = lazy(() => import("@/app/(content)/vocabulary/page"));
const VocabularyQuizPage = lazy(
  () => import("@/app/(content)/vocabulary/quiz/page")
);
const VocabularyQuizPracticePage = lazy(
  () => import("@/app/(content)/vocabulary/quiz/practice/page")
);
const NoticePage = lazy(() => import("@/app/(content)/notice/page"));
const NoticeDetailPage = lazy(
  () => import("@/app/(content)/notice/[id]/page")
);
const TasksPage = lazy(() => import("@/app/(content)/tasks/page"));
const TasksCalendarPage = lazy(
  () => import("@/app/(content)/tasks/calendar/page")
);
const TaskDetailPage = lazy(() => import("@/app/(content)/tasks/[id]/page"));
const VocabularyQuizChallengePage = lazy(
  () => import("@/app/(content)/vocabulary/quiz/challenge/[id]/page")
);
const SettingsPage = lazy(() => import("@/app/(content)/settings/page"));
const JoinClassPage = lazy(() => import("@/app/(content)/join-class/page"));
const MyClassesPage = lazy(() => import("@/app/(content)/my-classes/page"));

const PendingApprovalPage = lazy(() => import("@/app/pending-approval/page"));
const TermPage = lazy(() => import("@/app/term/[id]/page"));
const SupportPage = lazy(() => import("@/app/support/page"));

/** lazy 컴포넌트를 Suspense로 감싸 라우트 element로 사용하기 위한 헬퍼 */
function withSuspense(element: ReactNode) {
  return <Suspense fallback={<CommonLoading />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      // Auth routes (redirect to /main if already logged in)
      {
        element: <AuthGuard />,
        children: [
          {
            path: "auth/login",
            element: <LoginPage />,
          },
          {
            path: "auth/signup/*",
            element: <SignupPage />,
          },
          {
            path: "auth/find-id",
            element: <FindIdPage />,
          },
          {
            path: "auth/find-password",
            element: <FindPasswordPage />,
          },
        ],
      },

      // Protected routes (require authentication)
      {
        element: <ProtectedRoute />,
        children: [
          // Content layout routes
          {
            element: <ContentLayout />,
            children: [
              {
                path: "main",
                element: withSuspense(<MainPage />),
              },
              {
                path: "mypage",
                element: withSuspense(<MypagePage />),
              },
              {
                path: "vocabulary",
                element: withSuspense(<VocabularyPage />),
              },
              {
                path: "vocabulary/quiz",
                element: withSuspense(<VocabularyQuizPage />),
              },
              {
                path: "vocabulary/quiz/practice",
                element: withSuspense(<VocabularyQuizPracticePage />),
              },
              {
                path: "notice",
                element: withSuspense(<NoticePage />),
              },
              {
                path: "notice/:id",
                element: withSuspense(<NoticeDetailPage />),
              },
              {
                path: "tasks",
                element: withSuspense(<TasksPage />),
              },
              {
                path: "tasks/calendar",
                element: withSuspense(<TasksCalendarPage />),
              },
              {
                path: "tasks/:id",
                element: withSuspense(<TaskDetailPage />),
              },
              {
                path: "vocabulary/quiz/challenge/:id",
                element: withSuspense(<VocabularyQuizChallengePage />),
              },
              {
                path: "settings",
                element: withSuspense(<SettingsPage />),
              },
              {
                path: "join-class",
                element: withSuspense(<JoinClassPage />),
              },
              {
                path: "my-classes",
                element: withSuspense(<MyClassesPage />),
              },
            ],
          },
          // Pending approval page (protected but no content layout)
          {
            path: "pending-approval",
            element: withSuspense(<PendingApprovalPage />),
          },
        ],
      },

      // Public routes
      {
        path: "term/:id",
        element: withSuspense(<TermPage />),
      },
      {
        path: "support",
        element: withSuspense(<SupportPage />),
      },

      // Default redirect
      {
        index: true,
        element: <Navigate to="/auth/login" replace />,
      },
    ],
  },
]);
